import React, { useMemo, useRef, useState, Children, useEffect } from "react";

type SimpleSliderProps = {
  children: React.ReactNode;       // Cualquier JSX como slides
  initialIndex?: number;
  loop?: boolean;                   // true: vuelve al inicio/fin
  className?: string;               // clases extra para el contenedor
  onIndexChange?: (index: number) => void;
  autoplay?: boolean;               // 👈 nuevo (default: false)
  time?: number;                    // 👈 nuevo en ms (default: 2000)
};

export default function SimpleSlider({
  children,
  initialIndex = 0,
  loop = true,
  className,
  onIndexChange,
  autoplay = false,   // default
  time = 2000,        // default
}: SimpleSliderProps) {
  const slides = useMemo(() => Children.toArray(children), [children]);
  const count = slides.length;

  const [index, setIndex] = useState<number>(() => {
    if (count === 0) return 0;
    return Math.max(0, Math.min(initialIndex, count - 1));
  });

  // Mantener index dentro de rango si cambia el número de slides
  useEffect(() => {
    if (count === 0) {
      setIndex(0);
    } else if (index > count - 1) {
      setIndex(count - 1);
    }
  }, [count, index]);

  // Notificar cambios de índice
  useEffect(() => {
    if (onIndexChange) onIndexChange(index);
  }, [index, onIndexChange]);

  const go = (next: number) => {
    if (count === 0) return;
    let i = next;
    if (loop) {
      i = (next + count) % count;
    } else {
      i = Math.max(0, Math.min(next, count - 1));
    }
    setIndex(i);
  };

  const prev = () => go(index - 1);
  const next = () => go(index + 1);

  // Autoplay
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    // Limpiar por si acaso
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!autoplay || count <= 1) return;

    // Usar actualización funcional para evitar cierres obsoletos
    timerRef.current = window.setInterval(() => {
      setIndex((prev) => {
        if (count === 0) return 0;
        const nextIdx = loop ? (prev + 1) % count : Math.min(prev + 1, count - 1);
        return nextIdx;
      });
    }, Math.max(0, time));

    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [autoplay, time, count, loop]);

  // Swipe básico
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const threshold = 40;

  const onTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
  };

  const onTouchEnd: React.TouchEventHandler<HTMLDivElement> = (e) => {
    if (startX.current === null || startY.current === null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;
    startX.current = null;
    startY.current = null;

    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > threshold) {
      if (dx < 0) next();
      else prev();
    }
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === "ArrowLeft") prev();
    if (e.key === "ArrowRight") next();
  };

  if (count === 0) return null;

  return (
    <div
      className={`relative w-full bg-transparent ${className ?? ""}`}
      role="region"
      aria-roledescription="carousel"
      aria-label="slider"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Slide actual */}
      <div className="w-full h-full">
        {slides[index]}
      </div>

      {/* Botones laterales */}
      {count > 1 && (
        <>
          <button
            type="button"
            aria-label="Anterior"
            onClick={prev}
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full px-3 py-2 bg-black/40 text-white hover:bg-black/55 focus:outline-none z-1000 hover:cursor-pointer"
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Siguiente"
            onClick={next}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full px-3 py-2 bg-black/40 text-white hover:bg-black/55 focus:outline-none z-1000 hover:cursor-pointer"
          >
            ›
          </button>
        </>
      )}
    </div>
  );
}
